import {
  getAssessmentScoreCategory,
  type AssessmentScoreColor,
} from "@repo/app-types/constants/assessmentScore";
import { Color } from "@repo/app-types/constants/colors";
import {
  getPassScoreCategory,
  type PassScoreColor,
} from "@repo/app-types/constants/passScore";
import type { CriteriaScoreDistribution } from "@repo/app-types/entities/teammatePerformanceSummary";
import { stringifyGradingValue } from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { useCallback, useMemo, type FC } from "react";
import {
  ToggleButton,
  ToggleButtonGroup,
  type Key,
} from "react-aria-components";
import { tv } from "tailwind-variants";

export interface CriterionScoreDistributionBarProps {
  distribution: CriteriaScoreDistribution | undefined;
  selectedValue: string | null;
  onValueSelect: (value: string | null) => void;
  isLoading?: boolean;
}

const bucketDefinitionsByValueType: Record<
  CriteriaScoreDistribution["valueType"],
  {
    value: string;
    color: AssessmentScoreColor | PassScoreColor | typeof Color.Gray;
    label: string;
  }[]
> = {
  "1_5_score": [
    {
      value: stringifyGradingValue({ type: "1_5_score", value: 1 }),
      ...getAssessmentScoreCategory(1),
    },
    {
      value: stringifyGradingValue({ type: "1_5_score", value: 2 }),
      ...getAssessmentScoreCategory(2),
    },
    {
      value: stringifyGradingValue({ type: "1_5_score", value: 3 }),
      ...getAssessmentScoreCategory(3),
    },
    {
      value: stringifyGradingValue({ type: "1_5_score", value: 4 }),
      ...getAssessmentScoreCategory(4),
    },
    {
      value: stringifyGradingValue({ type: "1_5_score", value: 5 }),
      ...getAssessmentScoreCategory(5),
    },
    {
      value: stringifyGradingValue({ type: "1_5_score", value: null }),
      label: "N/A",
      color: Color.Gray,
    },
  ],
  yes_no: [
    {
      value: stringifyGradingValue({ type: "yes_no", value: false }),
      ...getPassScoreCategory(false),
    },
    {
      value: stringifyGradingValue({ type: "yes_no", value: true }),
      ...getPassScoreCategory(true),
    },
    {
      value: stringifyGradingValue({ type: "yes_no", value: null }),
      label: "N/A",
      color: Color.Gray,
    },
  ],
};

const buttonStyle = tv({
  base: "-ml-[1px] flex flex-auto items-center justify-between gap-2 border border-border-light bg-white px-3 whitespace-nowrap text-gray-600 shadow-xs first-of-type:ml-0 first-of-type:rounded-l-lg last-of-type:rounded-r-lg hover:bg-gray-50 data-[implicitly-selected]:z-10",
  variants: {
    /* prettier-ignore */
    color: {
      [Color.Red]: "data-[implicitly-selected]:border-red-300 data-[implicitly-selected]:bg-red-300 data-[implicitly-selected]:hover:bg-red-300 data-[implicitly-selected]:text-red-900",
      [Color.Orange]: "data-[implicitly-selected]:border-orange-300 data-[implicitly-selected]:bg-orange-300 data-[implicitly-selected]:hover:bg-orange-300 data-[implicitly-selected]:text-orange-900",
      [Color.Amber]: "data-[implicitly-selected]:border-amber-300 data-[implicitly-selected]:bg-amber-300 data-[implicitly-selected]:hover:bg-amber-300 data-[implicitly-selected]:text-amber-900",
      [Color.Yellow]: "data-[implicitly-selected]:border-yellow-300 data-[implicitly-selected]:bg-yellow-300 data-[implicitly-selected]:hover:bg-yellow-300 data-[implicitly-selected]:text-yellow-900",
      [Color.Lime]: "data-[implicitly-selected]:border-lime-300 data-[implicitly-selected]:bg-lime-300 data-[implicitly-selected]:hover:bg-lime-300 data-[implicitly-selected]:text-lime-900",
      [Color.Green]: "data-[implicitly-selected]:border-green-300 data-[implicitly-selected]:bg-green-300 data-[implicitly-selected]:hover:bg-green-300 data-[implicitly-selected]:text-green-900",
      [Color.Emerald]: "data-[implicitly-selected]:border-emerald-300 data-[implicitly-selected]:bg-emerald-300 data-[implicitly-selected]:hover:bg-emerald-300 data-[implicitly-selected]:text-emerald-900",
      [Color.DarkEmerald]: "data-[implicitly-selected]:border-emerald-500 data-[implicitly-selected]:bg-emerald-500 data-[implicitly-selected]:hover:bg-emerald-500 data-[implicitly-selected]:text-emerald-900",
      [Color.Blue]: "data-[implicitly-selected]:border-blue-300 data-[implicitly-selected]:bg-blue-300 data-[implicitly-selected]:hover:bg-blue-300 data-[implicitly-selected]:text-blue-900",
      [Color.Gray]: "data-[implicitly-selected]:border-border-strong data-[implicitly-selected]:bg-gray-300 data-[implicitly-selected]:hover:bg-gray-300 data-[implicitly-selected]:text-foreground",
    },
  },
});

export const CriterionScoreDistributionBar: FC<
  CriterionScoreDistributionBarProps
> = (props) => {
  const buckets = useMemo(() => {
    const distribution = props.distribution;

    if (!distribution) {
      return undefined;
    }

    const bucketDefinitions =
      bucketDefinitionsByValueType[distribution.valueType];
    const totalCount = bucketDefinitions.reduce(
      (acc, { value }) => acc + (distribution.counts[value] ?? 0),
      0,
    );
    return bucketDefinitions
      .map((bucketDefinition) => ({
        ...bucketDefinition,
        count: distribution.counts[bucketDefinition.value] ?? 0,
        fraction:
          (distribution.counts[bucketDefinition.value] ?? 0) / totalCount,
      }))
      .filter(({ count }) => count > 0);
  }, [props.distribution]);

  const selectedValue = props.selectedValue;
  const selectedKeys = useMemo(() => {
    return selectedValue ? [selectedValue] : [];
  }, [selectedValue]);

  const onSelectionChange = useCallback(
    (keys: Set<Key>) => {
      const key = keys.values().next().value;
      props.onValueSelect(typeof key === "string" ? key : null);
    },
    [props.onValueSelect],
  );

  return (
    <ToggleButtonGroup
      className="contain flex h-8 w-full items-stretch justify-stretch"
      selectionMode="single"
      selectedKeys={selectedKeys}
      onSelectionChange={onSelectionChange}
    >
      {buckets?.map((bucket) => (
        <ToggleButton
          key={bucket.value}
          id={bucket.value}
          className={buttonStyle({ color: bucket.color })}
          data-implicitly-selected={
            selectedValue === bucket.value ||
            (selectedValue === null && bucket.value !== "N/A")
              ? "true"
              : undefined
          }
          style={{
            flexBasis: `${(bucket.fraction * 100).toFixed(1)}%`,
          }}
        >
          <span className="text-sm font-semibold">{bucket.label}</span>
          <span className="text-xs">
            {bucket.count}{" "}
            {isFinite(bucket.fraction)
              ? `(${(bucket.fraction * 100).toFixed(0)}%)`
              : null}
          </span>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};
